import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { testSupabaseConnectivity } from '../../lib/supabase';
import { CheckCircle, XCircle, AlertCircle, User, LogOut, Settings } from 'lucide-react';

export const QuickAuthTest: React.FC = () => {
  const { user, isAuthenticated, login, logout, signup } = useAuth();
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testPassword, setTestPassword] = useState('testpass123');
  const [testName, setTestName] = useState('Test User');
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const testConnection = async () => {
    setConnectionStatus('testing');
    setConnectionError('');
    
    try {
      const result = await testSupabaseConnectivity();
      if (result.success) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
        setConnectionError(result.error || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthMessage('');
    
    try {
      const result = await login(testEmail, testPassword);
      if (result.success) {
        setAuthMessage('✅ Login successful!');
      } else {
        setAuthMessage(`❌ Login failed: ${result.error}`);
      }
    } catch (error) {
      setAuthMessage(`❌ Login error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignup = async () => {
    setAuthLoading(true);
    setAuthMessage('');
    
    try {
      const result = await signup(testName, testEmail, testPassword);
      if (result.success) {
        setAuthMessage('✅ Signup successful! Check your email for confirmation.');
      } else {
        setAuthMessage(`❌ Signup failed: ${result.error}`);
      }
    } catch (error) {
      setAuthMessage(`❌ Signup error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    setAuthLoading(true);
    setAuthMessage('');
    
    try {
      const result = await logout();
      if (result.success) {
        setAuthMessage('✅ Logged out successfully!');
      } else {
        setAuthMessage(`❌ Logout failed: ${result.error}`);
      }
    } catch (error) {
      setAuthMessage(`❌ Logout error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <Settings className="h-6 w-6 mr-2 text-blue-600" />
            PromptOS Supabase Auth Test
          </h1>

          {/* Environment Variables Check */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Environment Variables</h2>
            <div className="space-y-2">
              <div className="flex items-center">
                {import.meta.env.VITE_SUPABASE_URL ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="text-sm">
                  VITE_SUPABASE_URL: {import.meta.env.VITE_SUPABASE_URL ? '✓ Set' : '✗ Missing'}
                </span>
              </div>
              <div className="flex items-center">
                {import.meta.env.VITE_SUPABASE_ANON_KEY ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="text-sm">
                  VITE_SUPABASE_ANON_KEY: {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✓ Set' : '✗ Missing'}
                </span>
              </div>
            </div>
          </div>

          {/* Connection Test */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Connection Test</h2>
            <button
              onClick={testConnection}
              disabled={connectionStatus === 'testing'}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {connectionStatus === 'testing' ? 'Testing...' : 'Test Supabase Connection'}
            </button>
            
            {connectionStatus === 'success' && (
              <div className="mt-2 flex items-center text-green-600">
                <CheckCircle className="h-5 w-5 mr-2" />
                Connection successful!
              </div>
            )}
            
            {connectionStatus === 'error' && (
              <div className="mt-2 flex items-start text-red-600">
                <XCircle className="h-5 w-5 mr-2 mt-0.5" />
                <div>
                  <p>Connection failed:</p>
                  <p className="text-sm mt-1">{connectionError}</p>
                </div>
              </div>
            )}
          </div>

          {/* Current Auth Status */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Auth Status</h2>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              {isAuthenticated ? (
                <div className="flex items-center text-green-600">
                  <User className="h-5 w-5 mr-2" />
                  <div>
                    <p>Logged in as: <strong>{user?.email}</strong></p>
                    <p className="text-sm">Name: {user?.name}</p>
                    <p className="text-sm">ID: {user?.id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Not authenticated
                </div>
              )}
            </div>
          </div>

          {/* Test Credentials */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Test Credentials</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Name"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <input
                type="email"
                placeholder="Email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <input
                type="password"
                placeholder="Password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Auth Actions */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Test Actions</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSignup}
                disabled={authLoading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {authLoading ? 'Loading...' : 'Test Signup'}
              </button>
              <button
                onClick={handleLogin}
                disabled={authLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {authLoading ? 'Loading...' : 'Test Login'}
              </button>
              {isAuthenticated && (
                <button
                  onClick={handleLogout}
                  disabled={authLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {authLoading ? 'Loading...' : 'Logout'}
                </button>
              )}
            </div>
          </div>

          {/* Auth Messages */}
          {authMessage && (
            <div className="mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm font-mono">{authMessage}</p>
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              <a href="/auth" className="text-blue-600 hover:underline text-sm">Auth Form</a>
              <a href="/dashboard" className="text-blue-600 hover:underline text-sm">Dashboard</a>
              <a href="/" className="text-blue-600 hover:underline text-sm">Home</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
